import React, { memo, forwardRef, useImperativeHandle, useState, useCallback } from 'react'
import MuiAutocomplete from '@material-ui/lab/Autocomplete'
import PropTypes from 'prop-types'
import TextField from '@material-ui/core/TextField'
import { useTheme, makeStyles } from '@material-ui/core/styles'

import { standardBodyFont } from '../../themes'
import { translate } from '../../multilanguage'

const inputMarginTop = '-1px'
const inputLabelMarginTop = '-4px'

const useStyles = makeStyles(theme => ({
  paper: {
    ...standardBodyFont(theme),
  }
}))

/**
 * Implements Mui Autocomplete.
 *
 * @param {object} props component props
 * @returns {Autocomplete} Autocomplete component
 */
const Autocomplete = memo(forwardRef((props, ref) => {
  const [value, setValue] = useState(null)
  const theme = useTheme()
  const classes = useStyles()
  let { noTranslate, noTranslateItems, error, ...finalProps } = props
  if (error === false) {
    error = undefined
  }

  const getOptionLabel = useCallback(entry => {
    // If noTranslateItems is true, don't translate the option labels
    if (noTranslateItems) {
      return entry.label
    }
    // Optionally translate if needed, or just return the label
    return entry.label
  }, [noTranslateItems])

  const getOptionSelected = useCallback((entry, value) => {
    return entry.value === value.value
  }, [])

  const getOptionValue = item => {
    if (item === null) {
      return null
    }
    if (props.getOptionValue) {
      return props.getOptionValue(item)
    }
    return item.value
  }

  const onChange = (_event, item) => {
    setValue(item)
    props.onChange && props.onChange(getOptionValue(item))
  }

  useImperativeHandle(ref, () => ({
    getValue: () => getOptionValue(value),
    setValue: value => {
      let item = props.options?.find(item => getOptionValue(item) === value)
      if (item === undefined) {
        item = null
      }
      setValue(item)
      props.onChange && props.onChange(value)
    }
  }))

  return (
    <MuiAutocomplete
      {...finalProps}
      classes={classes}
      fullWidth
      options={props.options}
      getOptionLabel={props.getOptionLabel || getOptionLabel}
      getOptionSelected={props.getOptionSelected || getOptionSelected}
      value={value}
      onChange={onChange}
      renderInput={params => {
        params.inputProps.style = { ...params.inputProps.style, ...standardBodyFont(theme), ...props?.inputProps?.style, marginTop: inputMarginTop }
        params.InputLabelProps.style = { ...params.InputLabelProps.style, ...standardBodyFont(theme), ...props?.InputLabelProps?.style, marginTop: inputLabelMarginTop }
        return <TextField {...params} error={props.error} label={props.noTranslate ? props.label : translate(props.label)} fullWidth />
      }}
    />
  )
}))

Autocomplete.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Options Array for the control.
   */
  options: PropTypes.array.isRequired,
  /**
   Used to determine the string value for a given option. It's used to fill the input (and the list box options).
   Signature: function(option: T) => string
   */
  getOptionLabel: PropTypes.func,
  /**
   Given an item in options, this returns the value/code of the item.
   */
  getOptionValue: PropTypes.func, 
  /**
   Used to determine which option in the array was selected.
   Signature: function(option: T, optionSelected: T) => string
   */
  getOptionSelected: PropTypes.func,
  /**
   Label to display for the control.
   */
  label: PropTypes.string,
  /**
   onChange function called when an options is selected.
   */
  onChange: PropTypes.func,
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Displays the control in an error state.
   */
  error: PropTypes.bool,
  /**
   Props applied to the Input element.
   */
  inputProps: PropTypes.object,
  /**
   Props applied to the label.
   */
  InputLabelProps: PropTypes.object,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool
}

Autocomplete.defaultProps = {
  noTranslate: false,
}

export default Autocomplete